/**
 * Tiny in-memory emitter used by the imperative provider stores
 * (ModalsProvider, DrawersProvider, AlertsProvider). Each store is a
 * singleton that lives outside React; the matching `<Provider>` subscribes
 * via `useSyncExternalStore` and renders the resulting stack.
 *
 * Pattern: `openX()` pushes onto the stack and returns an id; `closeX(id)`
 * marks it closed (so the entrance/exit animation can play); a follow-up
 * `removeX(id)` evicts after the animation finishes.
 */

export type StackItem<P> = {
  id: string;
  /** True while the underlying overlay is visible. Toggled to false to
   *  trigger the exit animation; the provider then calls `remove` to evict. */
  opened: boolean;
  props: P;
};

export interface StackStore<P> {
  getState(): StackItem<P>[];
  subscribe(listener: () => void): () => void;
  open(props: P, id?: string): string;
  close(id: string): void;
  remove(id: string): void;
  update(id: string, patch: Partial<P>): void;
  closeAll(): void;
}

let counter = 0;
function nextId(prefix: string): string {
  counter += 1;
  return `${prefix}-${counter}-${Date.now().toString(36)}`;
}

export function createStackStore<P>(prefix: string): StackStore<P> {
  let state: StackItem<P>[] = [];
  const listeners = new Set<() => void>();

  function emit() {
    for (const l of listeners) l();
  }

  return {
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    open(props, id) {
      const itemId = id ?? nextId(prefix);
      state = [...state, { id: itemId, opened: true, props }];
      emit();
      return itemId;
    },
    close(id) {
      state = state.map((it) => (it.id === id ? { ...it, opened: false } : it));
      emit();
    },
    remove(id) {
      state = state.filter((it) => it.id !== id);
      emit();
    },
    update(id, patch) {
      state = state.map((it) => (it.id === id ? { ...it, props: { ...it.props, ...patch } } : it));
      emit();
    },
    closeAll() {
      state = state.map((it) => ({ ...it, opened: false }));
      emit();
    }
  };
}
